import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, Sparkles } from "lucide-react";
import { SERVICES } from "../data/staticData";
import { useAuth } from "../lib/auth";

const NavLinks = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass-nav" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="h-8 w-8 rounded-xl bg-ink text-white grid place-items-center">
              <Sparkles size={16} strokeWidth={2.2} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Zenith Ranking</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-lg transition ${isActive ? "text-ink" : "text-inkSoft hover:text-ink"}`
              }
              data-testid="nav-home"
            >
              Home
            </NavLink>

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/services"
                className="px-3 py-2 text-sm font-medium rounded-lg text-inkSoft hover:text-ink inline-flex items-center gap-1 transition"
                data-testid="nav-services-trigger"
                onClick={() => setServicesOpen(false)}
              >
                Services <ChevronDown size={14} />
              </Link>
              {servicesOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                  data-testid="nav-services-dropdown"
                >
                  <div className="bg-white rounded-2xl border border-line shadow-[0_30px_80px_rgba(0,0,0,0.08)] p-3 w-[640px] grid grid-cols-2 gap-1">
                    {SERVICES.map((s) => {
                      const Icon = s.icon;
                      return (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="flex items-start gap-3 p-3 rounded-xl hover:bg-surfaceAlt transition"
                          data-testid={`nav-service-${s.slug}`}
                        >
                          <div className="h-9 w-9 rounded-lg bg-surfaceAlt grid place-items-center text-terra mt-0.5">
                            <Icon size={18} strokeWidth={1.8} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-ink">{s.title}</div>
                            <div className="text-xs text-inkSoft line-clamp-1">{s.short}</div>
                          </div>
                        </Link>
                      );
                    })}
                    <Link
                      to="/services"
                      className="col-span-2 mt-1 flex items-center justify-between px-3 py-3 rounded-xl bg-ink text-white text-sm font-medium"
                      data-testid="nav-services-all"
                    >
                      View all services <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {NavLinks.slice(1).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-lg transition ${isActive ? "text-ink" : "text-inkSoft hover:text-ink"}`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <button
                className="btn-ghost text-sm"
                onClick={() => nav("/dashboard")}
                data-testid="navbar-dashboard-btn"
              >
                Dashboard
              </button>
            ) : (
              <Link to="/login" className="btn-ghost text-sm" data-testid="navbar-login-btn">
                Login
              </Link>
            )}
            <Link to={user ? "/dashboard/new-order" : "/signup"} className="btn-primary text-sm" data-testid="navbar-cta-btn">
              Get Started
            </Link>
          </div>

          <button
            className="lg:hidden h-10 w-10 grid place-items-center rounded-lg border border-line"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-line bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            {NavLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="block px-3 py-2 text-sm font-medium rounded-lg hover:bg-surfaceAlt"
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/services"
              className="block px-3 py-2 text-sm font-medium rounded-lg hover:bg-surfaceAlt"
              onClick={() => setMobileOpen(false)}
            >
              All Services
            </Link>
            <div className="pt-3 flex gap-2">
              {user ? (
                <Link to="/dashboard" className="btn-ghost flex-1 justify-center text-sm" onClick={() => setMobileOpen(false)}>
                  Dashboard
                </Link>
              ) : (
                <Link to="/login" className="btn-ghost flex-1 justify-center text-sm" onClick={() => setMobileOpen(false)}>
                  Login
                </Link>
              )}
              <Link to="/signup" className="btn-primary flex-1 justify-center text-sm" onClick={() => setMobileOpen(false)}>
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
